import { HowToAnswer } from "./HowToAnswer";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";

/**
 * CardRankTop2 — pick your first and second from a small set. Only two
 * slots exist. Tap once for first, tap again for second, tap a picked
 * card to unset it. The set is under 6 cards; sorting all of them is
 * a workday-sort question, not a top-2 question.
 *
 * Highest-evidence item in the discovery flow — the "bad day, nobody
 * watching" prompt uses this — so the interaction has to feel small
 * and low-stakes, never like a rank-order test.
 */

export interface Top2Item {
  id: string;
  label: string;
  detail?: string;
}

export interface CardRankTop2Props {
  items: Top2Item[];
  onComplete: (first: string, second: string) => void;
  ctaLabel?: string;
  labels?: { first: string; second: string };
}

export function CardRankTop2({
  items, onComplete, ctaLabel = "Done", labels = { first: "1st", second: "2nd" },
}: CardRankTop2Props) {
  const [first, setFirst] = useState<string | null>(null);
  const [second, setSecond] = useState<string | null>(null);

  function tap(id: string) {
    if (first === id) { setFirst(null); return; }
    if (second === id) { setSecond(null); return; }
    if (!first) { setFirst(id); return; }
    if (!second) { setSecond(id); return; }
    // Both full: replace second with the newest tap.
    setSecond(id);
  }

  const done = first && second;

  return (
    <div className="space-y-6">
      <HowToAnswer>Pick exactly 2: tap your first choice, then your second. Tap a pick again to remove it.</HowToAnswer>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((it) => {
          const isFirst = first === it.id;
          const isSecond = second === it.id;
          const active = isFirst || isSecond;
          return (
            <motion.button
              key={it.id}
              onClick={() => tap(it.id)}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.985 }}
              layout
              className={[
                "text-left rounded-3xl border p-4 md:p-5 min-h-[6rem] transition",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                active
                  ? "border-primary bg-primary/10"
                  : "border-border bg-card hover:border-primary/50",
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="text-base md:text-lg font-medium">{it.label}</div>
                  {it.detail && (
                    <div className="text-sm text-muted-foreground mt-1">{it.detail}</div>
                  )}
                </div>
                <AnimatePresence>
                  {isFirst && (
                    <motion.span
                      initial={{ scale: 0.6, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.6, opacity: 0 }}
                      className="shrink-0 h-8 px-3 rounded-full bg-primary text-primary-foreground text-xs font-medium grid place-items-center"
                    >
                      {labels.first}
                    </motion.span>
                  )}
                  {isSecond && (
                    <motion.span
                      initial={{ scale: 0.6, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.6, opacity: 0 }}
                      className="shrink-0 h-8 px-3 rounded-full bg-primary/30 text-primary text-xs font-medium grid place-items-center"
                    >
                      {labels.second}
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
            </motion.button>
          );
        })}
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => done && onComplete(first!, second!)}
          disabled={!done}
          className="min-h-[48px] px-6 rounded-2xl bg-primary text-primary-foreground font-medium
            disabled:opacity-40 disabled:pointer-events-none inline-flex items-center gap-2
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Check className="h-4 w-4" /> {ctaLabel}
        </button>
      </div>
    </div>
  );
}
